// components/HighlightableText/HighlightableText.jsx
import React from 'react'
import './HighlightableText.css'

/**
 * Componente para resaltar texto que coincide con términos de búsqueda
 * 
 * @param {string} text - Texto completo a mostrar
 * @param {string|array} highlight - Término(s) a resaltar
 * @param {string} className - Clase CSS para el contenedor
 * @param {string} highlightClassName - Clase CSS para el texto resaltado
 * @param {boolean} caseSensitive - Si la búsqueda es sensible a mayúsculas
 * @param {string} fallback - Texto a mostrar si text está vacío
 */
const HighlightableText = ({
	text = '',
	highlight = '',
	className = '',
	highlightClassName = 'highlighted-text',
	caseSensitive = false,
	fallback = ''
}) => {
	// Si no hay texto, mostrar fallback
	if (!text && fallback) {
		return <span className={className}>{fallback}</span>
	}

	// Si no hay texto, no mostrar nada
	if (!text) {
		return null
	}

	// Si no hay términos de búsqueda, mostrar texto normal
	if (!highlight || highlight.length === 0) {
		return <span className={className}>{text}</span>
	}

	// Normalizar highlight a array
	const highlightTerms = Array.isArray(highlight) ? highlight : [highlight]

	// Filtrar términos vacíos
	const validTerms = highlightTerms.filter(term => term && term.trim().length > 0)

	if (validTerms.length === 0) {
		return <span className={className}>{text}</span>
	}

	// Crear expresión regular para encontrar todos los términos
	const flags = caseSensitive ? 'g' : 'gi'
	const escapedTerms = validTerms.map(term =>
		term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escapar caracteres especiales
	)
	const regex = new RegExp(`(${escapedTerms.join('|')})`, flags)

	// Dividir el texto en partes
	const parts = text.split(regex)

	return (
		<span className={className}>
			{parts.map((part, index) => {
				// Verificar si esta parte coincide con algún término de búsqueda
				const isHighlighted = validTerms.some(term =>
					caseSensitive ? part === term : part.toLowerCase() === term.toLowerCase()
				)

				return isHighlighted ? (
					<mark key={index} className={highlightClassName}>
						{part}
					</mark>
				) : (
					<span key={index}>{part}</span>
				)
			})}
		</span>
	)
}

export default HighlightableText